import { Timestamp } from 'firebase/firestore';
import { Database } from '~/services/db/database';
import { ClientDatabase } from '~/types/client';
import { API } from '@unboared/react';

type Scope = 'daily' | 'weekly' | 'monthly' | 'yearly' | 'all-time';

function windowForScope(scope: Scope): { start?: Date; end?: Date } {
    const now = new Date();
    switch (scope) {
        case 'daily': {
            const start = new Date();
            start.setHours(0, 0, 0, 0);
            const end = new Date(start.getTime() + 24 * 60 * 60 * 1000);
            return { start, end };
        }
        case 'weekly': {
            const start = new Date();
            const day = start.getDay();
            const diff = (day + 6) % 7; // Monday as start
            start.setDate(start.getDate() - diff);
            start.setHours(0, 0, 0, 0);
            const end = new Date(start.getTime() + 7 * 24 * 60 * 60 * 1000);
            return { start, end };
        }
        case 'monthly': {
            const start = new Date(now.getFullYear(), now.getMonth(), 1);
            const end = new Date(now.getFullYear(), now.getMonth() + 1, 1);
            return { start, end };
        }
        case 'yearly': {
            const start = new Date(now.getFullYear(), 0, 1);
            const end = new Date(now.getFullYear() + 1, 0, 1);
            return { start, end };
        }
        case 'all-time':
            return { start: undefined, end: undefined };
    }
}

function addValidity(issuedAt: Date, validityDelay: '1h' | '3h' | '12h' | '1d'): Date {
    const map: Record<string, number> = {
        '1h': 60 * 60 * 1000,
        '3h': 3 * 60 * 60 * 1000,
        '12h': 12 * 60 * 60 * 1000,
        '1d': 24 * 60 * 60 * 1000,
    };
    const delta = map[validityDelay] ?? map['1h'];
    return new Date(issuedAt.getTime() + delta);
}

/**
 * An offer is eligible for a given screen when it has no screen restriction
 * (empty/undefined `screenIDs` → broadcast everywhere) or when the current
 * screen is explicitly in its target list. A targeted offer is never shown when
 * the screen is unknown (e.g. anim mode), so targeting can't leak elsewhere.
 */
function offerMatchesScreen(offer: any, screenID?: string): boolean {
    const targeted: string[] | undefined = offer?.screenIDs;
    if (!Array.isArray(targeted) || targeted.length === 0) return true;
    return !!screenID && targeted.includes(screenID);
}

async function getMaxScoreInWindow(
    clientID: string,
    gameID: string,
    start?: Date,
    end?: Date,
    excludeScoreId?: string
): Promise<number> {
    const fetchLimit = 10;
    let list = [];

    if (!start && !end) {
        const result = await Database.highscore.getSnapshot(clientID, gameID, fetchLimit, false);
        list = Array.isArray(result) ? result : [];
    } else if (start && end) {
        const result = await Database.highscore.getWindowSnapshot(clientID, gameID, start, end, fetchLimit, false);
        list = Array.isArray(result) ? result : [];
    }

    if (excludeScoreId) {
        list = list.filter((item) => item.id !== excludeScoreId);
    }

    const scoresToCompare = list.map((item) => item.score || 0);

    if (scoresToCompare.length === 0) {
        return 0;
    }
    return Math.max(...scoresToCompare);
}

export const HighscoreCouponService = {
    async handleGameEnded(params: {
        clientID: string;
        gameID: string;
        playerId: string;
        score: number;
        excludeScoreId?: string;
        screenID?: string;
    }) {
        const { clientID, gameID, playerId, score, excludeScoreId, screenID } = params;

        const windows: Scope[] = ['daily', 'weekly', 'monthly', 'yearly', 'all-time'];
        const scopeBeaten: Record<Scope, boolean> = {
            daily: false,
            weekly: false,
            monthly: false,
            yearly: false,
            'all-time': false,
        };

        const todayStart = new Date();
        todayStart.setHours(0, 0, 0, 0);
        const todayEnd = new Date(todayStart.getTime() + 24 * 60 * 60 * 1000);

        for (const scope of windows) {
            const { start, end } = windowForScope(scope);
            const maxInWindow = await getMaxScoreInWindow(clientID, gameID, start, end, excludeScoreId);

            if (score >= maxInWindow) {
                scopeBeaten[scope] = true;
            }
        }

        const scopePriority: Record<Scope, number> = {
            daily: 1,
            weekly: 2,
            monthly: 3,
            yearly: 4,
            'all-time': 5,
        };

        const beatenScopes = windows.filter((scope) => scopeBeaten[scope]);
        beatenScopes.sort((a, b) => scopePriority[b] - scopePriority[a]);

        // 'participation' is the lowest-priority fallback trigger: it is NOT a
        // record — any completed game with a score qualifies. Record-based offers
        // take precedence (higher in the list); if none apply, the player still
        // gets a participation offer when one is active. No early-return on
        // "no record beaten" anymore: participation must still fire. Maximises
        // coupon coverage (airport "send as many reductions as possible" goal).
        const candidateScopes: (Scope | 'participation')[] = [...beatenScopes, 'participation'];

        let selectedScope: Scope | 'participation' | null = null;
        let selectedOffers: any[] = [];

        for (const scope of candidateScopes) {
            const alreadyHasCouponForScope = await Database.coupons.existsTodayForScope(
                clientID,
                playerId,
                scope,
                todayStart,
                todayEnd,
                gameID
            );
            if (alreadyHasCouponForScope) {
                continue;
            }

            const offers = await Database.offers.retrieveActiveOffersByTrigger(clientID, scope, 1000);
            const eligibleOffers = (offers || []).filter((offer) => offerMatchesScreen(offer, screenID));
            if (eligibleOffers.length > 0) {
                selectedScope = scope;
                selectedOffers = eligibleOffers;
                break;
            }
        }

        if (!selectedScope || selectedOffers.length === 0) {
            return;
        }

        const scope = selectedScope;

        const selectedOffer = selectedOffers[Math.floor(Math.random() * selectedOffers.length)];

        try {
            let clientName: string | undefined;
            try {
                const client = await ClientDatabase.getClient(clientID);
                clientName = client.name;
            } catch (err) {
                console.warn('[HighscoreCouponService] Could not fetch client name:', err);
            }

            const issuedAt = new Date();
            const validUntil = addValidity(issuedAt, selectedOffer.validityDelay || '1h');
            const couponId = await Database.coupons.create(clientID, {
                playerId,
                offerId: selectedOffer.id,
                offerName: selectedOffer.title,
                validUntil: Timestamp.fromDate(validUntil),
                gameId: gameID,
                obtainedScore: score,
                leaderboardScope: scope,
                status: 'sent',
                issuedAt: Timestamp.fromDate(issuedAt),
                clientName,
            });

            try {
                const gamepadIDs = API.getGamepadIDs();
                for (const deviceID of gamepadIDs) {
                    const uid = API.getUID(deviceID);
                    if (uid === playerId) {
                        API.send(deviceID, '__couponWon__', {
                            coupon: {
                                id: couponId,
                                playerId,
                                offerId: selectedOffer.id,
                                offerName: selectedOffer.title,
                                issuedAt: Timestamp.fromDate(issuedAt),
                                validUntil: Timestamp.fromDate(validUntil),
                                gameId: gameID,
                                obtainedScore: score,
                                leaderboardScope: scope,
                                status: 'sent',
                                clientName,
                            },
                            offer: selectedOffer,
                        });
                        break;
                    }
                }
            } catch (err) {
                console.error('[HighscoreCouponService] Failed to send coupon event to gamepad:', err);
            }
        } catch (err) {
            console.error('[HighscoreCouponService] Error processing offers:', err);
        }
    },
};
